/**
 * Language Selector Component
 * Allows users to switch between supported languages
 */

import { useI18n } from "@/lib/i18n/hooks";
import { getSupportedLocales, SupportedLocale } from "@/lib/i18n/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Globe, Check } from "lucide-react";

interface LanguageSelectorProps {
  variant?: "default" | "ghost" | "outline";
  showLabel?: boolean;
  className?: string;
}

export function LanguageSelector({ 
  variant = "ghost", 
  showLabel = false,
  className = ""
}: LanguageSelectorProps) {
  const { locale, setLocale, isLoading } = useI18n();
  const locales = getSupportedLocales();
  const currentLocale = locales.find(l => l.code === locale);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={showLabel ? "default" : "icon"}
          className={className}
          disabled={isLoading}
        >
          <Globe className="h-4 w-4" />
          {showLabel && currentLocale && (
            <span className="ml-2">{currentLocale.flag} {currentLocale.nativeName}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {locales.map((loc) => (
          <DropdownMenuItem
            key={loc.code}
            onClick={() => setLocale(loc.code as SupportedLocale)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span>{loc.flag}</span>
              <span>{loc.nativeName}</span>
            </span>
            {locale === loc.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
